import { useMemo, useState } from 'react'
import {
  ExclamationTriangleIcon,
  ExternalLinkIcon,
  MagnifyingGlassIcon,
  ReloadIcon,
} from '@radix-ui/react-icons'

import { PageHeader } from '@/components/layout'
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui'
import { cn } from '@/lib/utils'
import type { AgingBucketKey, BillingCashDashboard, BillingCheck, BillingInvoice } from './types'
import { useBillingCash } from './useBillingCash'

type DetailView = 'customers' | 'invoices' | 'payments' | 'checks'

const AGING_LABELS: Record<AgingBucketKey, string> = {
  current: 'Current',
  pastDue1To30: '1–30 days',
  pastDue31To60: '31–60 days',
  pastDue61To90: '61–90 days',
  pastDue90Plus: '90+ days',
}

const AGING_COLORS: Record<AgingBucketKey, string> = {
  current: 'bg-success',
  pastDue1To30: 'bg-chart-1',
  pastDue31To60: 'bg-chart-4',
  pastDue61To90: 'bg-chart-5',
  pastDue90Plus: 'bg-destructive',
}

const DETAIL_LABELS: Record<DetailView, string> = {
  customers: 'Customers',
  invoices: 'Invoices',
  payments: 'Paid invoices',
  checks: 'Stable checks',
}

const formatCurrency = (cents: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(cents / 100)

const formatCurrencyExact = (cents: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(cents / 100)

const formatDate = (value: string | null) =>
  value
    ? new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(new Date(value))
    : 'No due date'

const formatDateTime = (value: string) =>
  new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value))

const sourceBadgeClass = (status: 'live' | 'error') =>
  status === 'live'
    ? 'border-success/40 bg-success/15 text-foreground'
    : 'border-destructive/40 bg-destructive/10 text-foreground'

function MetricStrip({ data }: { data: BillingCashDashboard }) {
  const metrics = [
    {
      label: 'Outstanding',
      value: data.summary.outstandingCents,
      detail: `${data.summary.openInvoiceCount} open invoices`,
    },
    {
      label: 'Past due',
      value: data.summary.pastDueCents,
      detail: `${Math.round(
        (data.summary.pastDueCents / Math.max(data.summary.outstandingCents, 1)) * 100,
      )}% of outstanding`,
    },
    {
      label: `Cash received · ${data.paymentWindowDays}d`,
      value: data.summary.cashReceivedCents,
      detail: 'Stripe payments plus unmatched cleared checks',
    },
    {
      label: 'Receivables · 30d cutoff',
      value: data.summary.expectedIncoming30DaysCents,
      detail: 'Overdue and upcoming invoices plus processing checks',
    },
  ]

  return (
    <Card>
      <CardContent className="grid p-0 sm:grid-cols-2 xl:grid-cols-4">
        {metrics.map((metric, index) => (
          <div
            key={metric.label}
            className={cn(
              'min-w-0 px-5 py-5',
              index > 0 && 'border-t border-border sm:border-l',
              index === 2 && 'sm:border-l-0 xl:border-l',
              index > 1 && 'xl:border-t-0',
            )}
          >
            <p className="text-sm font-medium text-muted-foreground">{metric.label}</p>
            <p
              className="mt-2 text-2xl font-semibold tabular-nums tracking-tight text-foreground"
              data-pii="financial"
            >
              {formatCurrency(metric.value)}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">{metric.detail}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

function AgingOverview({ data }: { data: BillingCashDashboard }) {
  const keys = Object.keys(AGING_LABELS) as AgingBucketKey[]
  const total = Math.max(data.summary.outstandingCents, 1)

  return (
    <Card>
      <CardContent className="space-y-5 p-5">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Invoice aging</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Open Stripe invoice balances grouped by days past due as of today.
          </p>
        </div>
        <div
          className="flex h-3 overflow-hidden rounded-full bg-muted"
          role="img"
          aria-label="Outstanding invoice balance by aging bucket"
        >
          {keys.map((key) => {
            const width = (data.aging[key].amountCents / total) * 100
            return width > 0 ? (
              <div
                key={key}
                className={AGING_COLORS[key]}
                style={{ width: `${width}%` }}
                title={`${AGING_LABELS[key]}: ${formatCurrencyExact(data.aging[key].amountCents)}`}
              />
            ) : null
          })}
        </div>
        <div className="grid gap-x-5 gap-y-4 sm:grid-cols-2 xl:grid-cols-5">
          {keys.map((key) => (
            <div key={key} className="min-w-0">
              <div className="flex items-center gap-2">
                <span className={cn('h-2.5 w-2.5 rounded-full', AGING_COLORS[key])} />
                <span className="text-sm text-muted-foreground">{AGING_LABELS[key]}</span>
              </div>
              <p className="mt-1 font-semibold tabular-nums text-foreground" data-pii="financial">
                {formatCurrency(data.aging[key].amountCents)}
              </p>
              <p className="text-xs text-muted-foreground">
                {data.aging[key].invoiceCount} invoice
                {data.aging[key].invoiceCount === 1 ? '' : 's'}
              </p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function CashReconciliation({ data }: { data: BillingCashDashboard }) {
  return (
    <div className="grid gap-5 xl:grid-cols-[1.35fr,1fr]">
      <Card>
        <CardContent className="space-y-5 p-5">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              Receivables through the 30-day cutoff
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Open invoices already due or due within 30 days, plus checks already processing at
              Stable. This is not weighted by collection likelihood.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-border bg-muted/25 p-4">
              <p className="text-sm text-muted-foreground">Invoices due</p>
              <p
                className="mt-2 text-xl font-semibold tabular-nums text-foreground"
                data-pii="financial"
              >
                {formatCurrency(data.summary.dueWithin30DaysCents)}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Processing-check matches are removed from this amount.
              </p>
            </div>
            <div className="rounded-lg border border-border bg-info/15 p-4">
              <p className="text-sm text-muted-foreground">Checks processing</p>
              <p
                className="mt-2 text-xl font-semibold tabular-nums text-foreground"
                data-pii="financial"
              >
                {formatCurrency(data.summary.processingChecksCents)}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {data.reconciliation.processingChecksMatched} matched to an open invoice.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-4 p-5">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Source status</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Live reads are cached for 2 minutes to limit provider traffic.
            </p>
          </div>
          <div className="space-y-2">
            {[
              ['Stripe invoices', data.sources.stripeInvoices],
              ['Stripe payments', data.sources.stripePayments],
              ['Stable checks', data.sources.stable],
            ].map(([label, status]) => (
              <div key={label} className="flex items-center justify-between gap-3 text-sm">
                <span className="text-foreground">{label}</span>
                <Badge variant="outline" className={sourceBadgeClass(status as 'live' | 'error')}>
                  {status === 'live' ? 'Live' : 'Unavailable'}
                </Badge>
              </div>
            ))}
          </div>
          <p className="border-t border-border pt-3 text-xs text-muted-foreground">
            Updated {formatDateTime(data.generatedAt)}. Only amount-and-payer matches are reconciled
            automatically.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}

function InvoiceStatus({ invoice }: { invoice: BillingInvoice }) {
  if (invoice.daysPastDue === 0) return <Badge variant="outline">Current</Badge>
  return (
    <Badge
      variant={invoice.daysPastDue > 90 ? 'destructive' : 'warning'}
      className="whitespace-nowrap"
    >
      {invoice.daysPastDue} days past due
    </Badge>
  )
}

function CheckStatus({ check }: { check: BillingCheck }) {
  if (check.isReversal) return <Badge variant="destructive">Reversed</Badge>
  if (check.status === 'completed') {
    return <Badge className="border-success/40 bg-success/15 text-foreground">Completed</Badge>
  }
  if (check.status === 'processing') return <Badge variant="warning">Processing</Badge>
  return <Badge variant="outline">{check.status}</Badge>
}

function DetailTable({ data }: { data: BillingCashDashboard }) {
  const [view, setView] = useState<DetailView>('customers')
  const [search, setSearch] = useState('')
  const [agingFilter, setAgingFilter] = useState<AgingBucketKey | 'all'>('all')
  const normalizedSearch = search.trim().toLocaleLowerCase('en-US')

  const filteredCustomers = useMemo(
    () =>
      data.customers.filter((customer) =>
        `${customer.name} ${customer.email || ''}`
          .toLocaleLowerCase('en-US')
          .includes(normalizedSearch),
      ),
    [data.customers, normalizedSearch],
  )
  const filteredInvoices = useMemo(
    () =>
      data.invoices.filter(
        (invoice) =>
          (agingFilter === 'all' || invoice.agingBucket === agingFilter) &&
          `${invoice.number || ''} ${invoice.customerName} ${invoice.customerEmail || ''}`
            .toLocaleLowerCase('en-US')
            .includes(normalizedSearch),
      ),
    [agingFilter, data.invoices, normalizedSearch],
  )
  const filteredPayments = useMemo(
    () =>
      data.payments.filter((payment) =>
        `${payment.invoiceNumber || ''} ${payment.customerName} ${payment.customerEmail || ''}`
          .toLocaleLowerCase('en-US')
          .includes(normalizedSearch),
      ),
    [data.payments, normalizedSearch],
  )
  const filteredChecks = useMemo(
    () =>
      data.checks.filter((check) =>
        `${check.payer || ''} ${check.memo || ''} ${check.status}`
          .toLocaleLowerCase('en-US')
          .includes(normalizedSearch),
      ),
    [data.checks, normalizedSearch],
  )

  const rowCount = {
    customers: filteredCustomers.length,
    invoices: filteredInvoices.length,
    payments: filteredPayments.length,
    checks: filteredChecks.length,
  }[view]

  return (
    <Card>
      <CardContent className="p-0">
        <div className="flex flex-col gap-4 border-b border-border p-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-1 rounded-lg bg-muted p-1" role="tablist">
            {(Object.keys(DETAIL_LABELS) as DetailView[]).map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={view === key}
                onClick={() => setView(key)}
                className={cn(
                  'rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  view === key
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {DETAIL_LABELS[key]}
              </button>
            ))}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            {view === 'invoices' ? (
              <Select
                value={agingFilter}
                onValueChange={(value) => setAgingFilter(value as AgingBucketKey | 'all')}
              >
                <SelectTrigger className="w-full sm:w-[165px]" aria-label="Filter invoices by age">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All ages</SelectItem>
                  {(Object.keys(AGING_LABELS) as AgingBucketKey[]).map((key) => (
                    <SelectItem key={key} value={key}>
                      {AGING_LABELS[key]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
            <div className="relative min-w-[260px]">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={`Search ${DETAIL_LABELS[view].toLocaleLowerCase('en-US')}`}
                className="pl-9"
                aria-label={`Search ${DETAIL_LABELS[view].toLocaleLowerCase('en-US')}`}
              />
            </div>
          </div>
        </div>

        <div className="max-h-[620px] overflow-auto">
          {view === 'customers' ? (
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-card">
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Invoices</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead className="text-right">Past due</TableHead>
                  <TableHead>Next due</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCustomers.map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell>
                      <p className="font-medium text-foreground" data-pii="name">
                        {customer.name}
                      </p>
                      <p className="text-xs text-muted-foreground" data-pii="email">
                        {customer.email || 'No billing email'}
                      </p>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {customer.invoiceCount}
                    </TableCell>
                    <TableCell
                      className="text-right font-semibold tabular-nums"
                      data-pii="financial"
                    >
                      {formatCurrencyExact(customer.outstandingCents)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums" data-pii="financial">
                      {formatCurrencyExact(customer.pastDueCents)}
                    </TableCell>
                    <TableCell>{formatDate(customer.nextDueAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}

          {view === 'invoices' ? (
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-card">
                <TableRow>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Remaining</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>
                      {invoice.hostedInvoiceUrl ? (
                        <a
                          href={invoice.hostedInvoiceUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
                        >
                          {invoice.number || invoice.id}
                          <ExternalLinkIcon />
                        </a>
                      ) : (
                        <span className="font-medium">{invoice.number || invoice.id}</span>
                      )}
                      <p className="mt-1 text-xs text-muted-foreground">
                        {invoice.collectionMethod === 'send_invoice'
                          ? 'Invoice sent'
                          : 'Auto-charge'}
                      </p>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium" data-pii="name">
                        {invoice.customerName}
                      </p>
                      <p className="text-xs text-muted-foreground" data-pii="email">
                        {invoice.customerEmail || 'No billing email'}
                      </p>
                    </TableCell>
                    <TableCell>{formatDate(invoice.dueAt)}</TableCell>
                    <TableCell>
                      <InvoiceStatus invoice={invoice} />
                    </TableCell>
                    <TableCell
                      className="text-right font-semibold tabular-nums"
                      data-pii="financial"
                    >
                      {formatCurrencyExact(invoice.amountRemainingCents)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}

          {view === 'payments' ? (
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-card">
                <TableRow>
                  <TableHead>Paid</TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Recorded through</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPayments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>{formatDate(payment.paidAt)}</TableCell>
                    <TableCell className="font-medium">
                      {payment.invoiceNumber || payment.invoiceId}
                    </TableCell>
                    <TableCell>
                      <p className="font-medium" data-pii="name">
                        {payment.customerName}
                      </p>
                      <p className="text-xs text-muted-foreground" data-pii="email">
                        {payment.customerEmail || 'No billing email'}
                      </p>
                    </TableCell>
                    <TableCell className="capitalize">
                      {payment.paymentType.replaceAll('_', ' ')}
                    </TableCell>
                    <TableCell
                      className="text-right font-semibold tabular-nums"
                      data-pii="financial"
                    >
                      {formatCurrencyExact(payment.amountCents)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}

          {view === 'checks' ? (
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-card">
                <TableRow>
                  <TableHead>Check</TableHead>
                  <TableHead>Payer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reconciliation</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredChecks.map((check) => (
                  <TableRow key={check.id}>
                    <TableCell>
                      <p className="font-medium">
                        {check.issueDate || formatDate(check.createdAt)}
                      </p>
                      <p className="text-xs text-muted-foreground" data-pii="content">
                        {check.memo || 'No memo'}
                      </p>
                    </TableCell>
                    <TableCell data-pii="name">{check.payer || 'Unknown payer'}</TableCell>
                    <TableCell>
                      <CheckStatus check={check} />
                    </TableCell>
                    <TableCell>
                      {check.matchedRecordId ? (
                        <div>
                          <p className="text-sm text-foreground">Matched</p>
                          <p className="text-xs text-muted-foreground">Amount and payer</p>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">Counted separately</span>
                      )}
                    </TableCell>
                    <TableCell
                      className="text-right font-semibold tabular-nums"
                      data-pii="financial"
                    >
                      {formatCurrencyExact(check.amountCents)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}

          {rowCount === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-muted-foreground">
              No {DETAIL_LABELS[view].toLocaleLowerCase('en-US')} match these filters.
            </div>
          ) : null}
        </div>
        <div className="border-t border-border px-4 py-3 text-xs text-muted-foreground">
          {rowCount} record{rowCount === 1 ? '' : 's'} shown
        </div>
      </CardContent>
    </Card>
  )
}

function DashboardSkeleton() {
  return (
    <div className="space-y-5 p-4 sm:p-6">
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-56 w-full" />
      <div className="grid gap-5 xl:grid-cols-2">
        <Skeleton className="h-60 w-full" />
        <Skeleton className="h-60 w-full" />
      </div>
      <Skeleton className="h-96 w-full" />
    </div>
  )
}

export function BillingCashPage() {
  const { data, error, isLoading, isFetching, paymentWindowDays, setPaymentWindowDays, refresh } =
    useBillingCash()
  const sourceErrors = data ? Object.values(data.sources.errors) : []

  return (
    <div className="min-h-full bg-background">
      <PageHeader
        title="Billing & cash"
        description="Preliminary Stripe invoice aging and Stable check clearing, reconciled into one cash view."
      >
        <Select
          value={String(paymentWindowDays)}
          onValueChange={(value) => setPaymentWindowDays(Number(value) as 7 | 30)}
        >
          <SelectTrigger className="w-[150px]" aria-label="Cash received window">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Paid · 7 days</SelectItem>
            <SelectItem value="30">Paid · 30 days</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={() => void refresh()} disabled={isFetching}>
          <ReloadIcon className={cn('mr-2 h-4 w-4', isFetching && 'animate-spin')} />
          Refresh data
        </Button>
      </PageHeader>

      {isLoading ? <DashboardSkeleton /> : null}

      {!isLoading && error ? (
        <div className="p-4 sm:p-6">
          <Alert className="border-destructive/50 bg-destructive/10">
            <ExclamationTriangleIcon />
            <AlertTitle>Billing data could not load</AlertTitle>
            <AlertDescription className="mt-2 text-muted-foreground">
              <p>{error.message}</p>
              <Button className="mt-4" onClick={() => void refresh()}>
                Try loading again
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      ) : null}

      {data ? (
        <main className="space-y-5 p-4 sm:p-6">
          {sourceErrors.length > 0 ? (
            <Alert className="border-warning/60 bg-warning/15">
              <ExclamationTriangleIcon />
              <AlertTitle>Some billing data is missing</AlertTitle>
              <AlertDescription className="mt-1 text-muted-foreground">
                {sourceErrors.join(' ')} Available source totals are still shown and are not
                presented as complete.
              </AlertDescription>
            </Alert>
          ) : null}
          <MetricStrip data={data} />
          <AgingOverview data={data} />
          <CashReconciliation data={data} />
          <DetailTable data={data} />
        </main>
      ) : null}
    </div>
  )
}

export default BillingCashPage
